import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { equals, not, propOr } from 'ramda';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import PricingsDisplayer from './PricingsDisplayer.jsx';
import { InfoMessage, Message, Tabs } from '@fasstech/middle-design-system';
import DisplayErrorMessage from '@@viewCommonComponents/displayer/DisplayErrorMessage';
import Title from '@@viewCommonComponents/displayer/Title';
import useGuaranteeLevelsForm from '../hooks/useGuaranteeLevelsForm';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import ComAndFrequencyButtons from './ComAndFrequencyButtons.jsx';
import { isNotNilOrEmpty } from '@@serverHelpers';
import { extraHandleSaveButton } from '../extra/extraHandleSaveButton';
import { useAnalytics } from '@@app/context/analytics-context.js';

const SimulationPricingsIndividualHealthForm = ({
  offer,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean,
  getGrantees,
  viewMode
}) => {
  const { trackEvent } = useAnalytics();
  const [displayError, setDisplayError] = useState({ display: false });
  const [showDocumentError, setShowDocumentError] = useState(false);

  const grantees = propOr([], 'beneficiaries', getGrantees);

  const customerViewMode = equals(viewMode, 'CUSTOMER');

  const {
    tabs,
    setTabs,
    updateVisibility,
    isButtonDisabled,
    displayTabsPagination,
    maxVisibleTabs,
    setSelectedGrantee
  } = useTabs({
    grantees,
    getMiddleText,
    filteredViewMode: customerViewMode
  });

  const {
    form,
    maxNumberOfProducts,
    $onClickPrev,
    onSubmit,
    isValid,
    commission,
    currentPricings,
    recommendedMessageInfo
  } =
    useGuaranteeLevelsForm({
      offer,
      getMiddleNumber,
      setDisplayError,
      selectedTab: tabs.selectedTab,
      grantees
    });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout withFormLayout={false} className={'flex flex-col items-center w-full gap-y-6'}>
          <Title
            title={
              customerViewMode
                ? getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_TITLE')
                : tabs.selectedTab === 0
                  ? getMiddleText('NIVEAUXGARANTIESETTARIFS_GARANTIES_PROPOSED_CUSTOMER_FORMULAS_TITLE')
                  : `${getMiddleText('NIVEAUXGARANTIESETTARIFS_GARANTIES_PROPOSED_GRANTEE_FORMULAS_TITLE')} ${tabs.selectedTab}`
            }
            variant={'secondary'}
          />

          <p
            className={'font-book text-md mt-3 col-span-2 text-center'}
            dangerouslySetInnerHTML={{
              __html: equals(maxNumberOfProducts, 1)
                ? getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_SINGULAR')
                : getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_DESCRIPTION_PLURAL', {
                  numberOfProducts: maxNumberOfProducts
                })
            }}></p>

          {getMiddleBoolean('PROCESSDEVENTE_TAUX_COMMISSION_EST_NECESSAIRE') &&
            isNotNilOrEmpty(commission?.rates) && (
            <ComAndFrequencyButtons
              rates={commission.rates}
              getMiddleBoolean={getMiddleBoolean}
              getMiddleText={getMiddleText}
            />
          )}

          <div className={'flex flex-col gap-4'}>
            <TabsPagination
              updateVisibility={updateVisibility}
              isButtonDisabled={isButtonDisabled}
              show={displayTabsPagination}
            >
              <Tabs
                items={tabs.tabs}
                selected={tabs.selectedTab}
                onClick={(index) => {
                  setTabs({
                    selectedTab: index,
                    tabs: tabs.tabs
                  });
                  const grantee = grantees[index];
                  if (grantee) {
                    setSelectedGrantee(grantee);
                  }
                }}
                isDisplayed={tabs.tabs.length > 1}
              />
            </TabsPagination>
          </div>

          {showDocumentError && (
            <Message
              variant="error"
              className="mt-2 text-sm"
              label={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_GENERAL_DOCUMENT_UNAVAILABLE_ERROR_MESSAGE'
              )}
            />
          )}

          <PricingsDisplayer
            getMiddleText={getMiddleText}
            maxNumberOfProducts={maxNumberOfProducts}
            setShowDocumentError={setShowDocumentError}
            setValue={form.setValue}
            offerPerPage={maxVisibleTabs}
            currentBeneficiaryIndex={tabs.selectedTab}
            currentPricings={currentPricings}
            selectedCommissionRate={commission.rate}
            viewMode={viewMode}
          />

          <InfoMessage iconName={'lightbulb'} className={'whitespace-pre-wrap w-full'}>
            {recommendedMessageInfo.recommendedProductMessage}
          </InfoMessage>

          <InfoMessage iconName={'lightbulb'} className={'whitespace-pre-wrap w-full'}>
            {recommendedMessageInfo.recommendedOptionMessage}
          </InfoMessage>

          {displayError.display && <DisplayErrorMessage text={displayError.message}/>}
        </PageLayout>

        <ButtonContainer
          errorMessage={offer.error}
          $onPreviousButtonClick={$onClickPrev}
          $onNextButtonClick={() => extraHandleSaveButton(trackEvent, form.getValues())}
          nextButtonIsDisabled={not(isValid) || offer.error}
          isFormError={not(isValid)}
          getMiddleText={getMiddleText}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          displayResumeButton
        />
      </form>
    </FormProvider>
  );
};

export default SimulationPricingsIndividualHealthForm;
